/**
 * Built-in icon set for map markers.
 *
 * Each icon is defined as SVG inner content (elements inside `<svg>`) for a
 * 24×24 viewBox, stroke-based with stroke-width="2".
 *
 * Icons are based on the Lucide icon set (https://lucide.dev, MIT license).
 * Users can register additional icons via `registerIcon()`.
 */

// ── Icon SVG inner content ──────────────────────────────────────────────────

const BUILTIN_ICONS: Record<string, string> = {
  // ── Map / navigation ────────────────────────────────────────────────────
  pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
  compass:
    '<circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88"/>',
  crosshair:
    '<circle cx="12" cy="12" r="10"/><line x1="22" y1="12" x2="18" y2="12"/><line x1="6" y1="12" x2="2" y2="12"/><line x1="12" y1="6" x2="12" y2="2"/><line x1="12" y1="22" x2="12" y2="18"/>',
  target:
    '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',
  flag: '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/>',
  mountain: '<path d="m8 3 4 8 5-5 5 15H2z"/>',

  // ── RPG / game ──────────────────────────────────────────────────────────
  sword:
    '<polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/><line x1="13" y1="19" x2="19" y2="13"/><line x1="16" y1="16" x2="20" y2="20"/><line x1="19" y1="21" x2="21" y2="19"/>',
  "shield-icon":
    '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>',
  crown:
    '<path d="m2 4 3 12h14l3-12-6 7-4-7-4 7z"/><path d="M5 16h14v4H5z"/>',
  castle:
    '<path d="M3 11h2V7h2v4h2V7h2v4h2V7h2v4h2V7h2v4h2v10H3z"/><rect x="9" y="14" width="6" height="7"/>',
  skull:
    '<circle cx="12" cy="10" r="7"/><path d="M9 22v-6"/><path d="M15 22v-6"/><circle cx="9.5" cy="10" r="1.5"/><circle cx="14.5" cy="10" r="1.5"/><path d="M10 16h4"/>',
  gem: '<path d="M6 3h12l4 6-10 13L2 9z"/><path d="M2 9h20"/><path d="m12 22 6-13"/><path d="m12 22-6-13"/>',
  scroll:
    '<path d="M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10"/><path d="M4 19a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2"/>',
  coins:
    '<circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><line x1="7" y1="6" x2="7.01" y2="6"/><line x1="16" y1="12" x2="16.01" y2="12"/>',
  axe: '<path d="m14 12-8.5 8.5a2.12 2.12 0 1 1-3-3L11 9"/><path d="M15 13 9 7l4-4 6 6z"/>',
  pickaxe:
    '<path d="M14 10 3 21"/><path d="M6 17l4-4"/><path d="M14 10c0-2.33 1.85-5 5-7 0 3-1 5.5-3 7.5"/><path d="M14 10c2.33 0 5 1.85 7 5-3 0-5.5-1-7.5-3"/>',

  // ── Nature ──────────────────────────────────────────────────────────────
  tree: '<path d="M12 3 4 15h5v7h6v-7h5z"/>',
  flame:
    '<path d="M8.5 14.5A4 4 0 0 1 8 12.5c0-2 1-3.5 2.5-5L12 5.5l1.5 2c1.5 1.5 2.5 3 2.5 5a4 4 0 0 1-.5 2"/><path d="M12 22c-4.2 0-7-3-7-7 0-3 2-6 4-8l3-3 3 3c2 2 4 5 4 8 0 4-2.8 7-7 7z"/>',

  // ── Structures ──────────────────────────────────────────────────────────
  house:
    '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
  tent: '<path d="M3.5 21 12 3l8.5 18"/><path d="M20.5 21H3.5"/><path d="M12 3v18"/>',
  anchor:
    '<circle cx="12" cy="5" r="3"/><line x1="12" y1="8" x2="12" y2="21"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/>',

  // ── Status / info ─────────────────────────────────────────────────────
  star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  heart:
    '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
  warning:
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  zap: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  eye: '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',

  // ── People / groups ─────────────────────────────────────────────────────
  users:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
};

// ── User-registered icons ─────────────────────────────────────────────────

const customIcons = new Map<string, string>();

/** Register a custom icon. `svgInner` is the content inside a 24×24 SVG. */
export function registerIcon(name: string, svgInner: string): void {
  customIcons.set(name, svgInner);
}

/** Get all available icon names. */
export function getIconNames(): string[] {
  return [
    ...Object.keys(BUILTIN_ICONS),
    ...customIcons.keys(),
  ];
}

/** Get the SVG inner content for an icon, or `null` if not found. */
export function getIconSvgContent(name: string): string | null {
  return customIcons.get(name) ?? BUILTIN_ICONS[name] ?? null;
}

/**
 * Build a full SVG string for the given icon, rendered at `sizePx` with `color`.
 * Returns `null` if the icon name is unknown.
 */
export function buildIconSvg(
  name: string,
  sizePx: number = 48,
  color: string = "white",
): string | null {
  const inner = getIconSvgContent(name);
  if (!inner) return null;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg"`,
    ` width="${sizePx}" height="${sizePx}" viewBox="0 0 24 24"`,
    ` fill="none" stroke="${color}" stroke-width="2"`,
    ` stroke-linecap="round" stroke-linejoin="round">`,
    inner,
    `</svg>`,
  ].join("");
}
